import React, { useState } from 'react';
import {
  Sliders,
  Activity,
  Key,
  Shield,
  Layers,
  Terminal,
  Server,
  Globe,
  Database,
  CheckCircle2,
  AlertTriangle,
  RefreshCw,
  Power,
  Zap,
  TrendingUp,
  Cpu,
  ArrowRight,
  Bell,
  Lock,
  Download
} from 'lucide-react';
import { AppView } from '../../types';
import {
  DEVELOPER_EXECUTIVE_METRICS,
  API_PRODUCTS,
  MOCK_PARTNERS,
  MOCK_PUBLIC_DATA_RIGHTS,
  MOCK_DEVELOPER_INCIDENTS
} from '../../data/developerMockData';
import { APIProduct, DeveloperIncident } from '../../services/developer/apiProductTypes';

type DevCommandTab =
  | 'overview'
  | 'products-traffic'
  | 'partners-governance'
  | 'kill-switches'
  | 'incidents'
  | 'data-rights';

interface Props {
  onNavigateToView: (v: AppView) => void;
}

export const DeveloperCommandCenter: React.FC<Props> = ({ onNavigateToView }) => {
  const [activeTab, setActiveTab] = useState<DevCommandTab>('overview');
  const [products, setProducts] = useState<APIProduct[]>(API_PRODUCTS);
  const [incidents, setIncidents] = useState<DeveloperIncident[]>(MOCK_DEVELOPER_INCIDENTS);
  const [killSwitchNotice, setKillSwitchNotice] = useState<string | null>(null);

  const toggleProductStatus = (productId: string) => {
    setProducts((prev) =>
      prev.map((p) => {
        if (p.id === productId) {
          const newStatus = p.status === 'ACTIVE' ? 'MAINTENANCE' : 'ACTIVE';
          setKillSwitchNotice(`Product [${p.name}] status switched to ${newStatus}`);
          return { ...p, status: newStatus };
        }
        return p;
      })
    );
    setTimeout(() => setKillSwitchNotice(null), 4000);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] pb-24 text-[#0F172A]">
      {/* Header Banner */}
      <div className="bg-[#0F172A] text-white pt-16 pb-24 px-6 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[28rem] h-[28rem] bg-[#1E40AF] rounded-full blur-[140px] opacity-25 pointer-events-none" />
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="flex items-center gap-3 text-[#94A3B8] mb-4 text-sm font-medium">
            <Sliders className="w-4 h-4 text-[#38BDF8]" />
            <span>YamooPay Platform Engineering</span>
            <span>/</span>
            <span className="text-white font-bold">Developer Platform Command Center (Prompt 20)</span>
          </div>

          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <h1 className="text-3xl md:text-5xl font-black tracking-tight text-white mb-3">
                Developer & Partner Command Center
              </h1>
              <p className="text-[#CBD5E1] text-base md:text-lg max-w-3xl leading-relaxed">
                Global telemetry, API product traffic, quota metering, partner governance, data redistribution rights, and emergency kill switches.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={() => onNavigateToView('developer-portal')}
                className="px-4 py-2.5 bg-[#1E40AF] hover:bg-[#1E3A8A] text-white rounded-xl text-xs font-bold flex items-center gap-2 transition-colors cursor-pointer"
              >
                <Globe className="w-4 h-4" />
                <span>Open Developer Portal</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Container */}
      <div className="max-w-7xl mx-auto px-6 -mt-12 relative z-20">
        <div className="bg-white rounded-2xl shadow-sm border border-[#E2E8F0] p-6 mb-8">
          {/* Sub Navigation Tabs */}
          <div className="flex flex-wrap gap-2 mb-8 border-b border-[#E2E8F0] pb-4">
            {[
              { id: 'overview', label: 'Executive Telemetry', icon: Activity },
              { id: 'products-traffic', label: 'API Products & Traffic', icon: Layers },
              { id: 'partners-governance', label: `Partner Integrations (${MOCK_PARTNERS.length})`, icon: Shield },
              { id: 'kill-switches', label: 'API Kill Switches & Circuit Breakers', icon: Power },
              { id: 'incidents', label: `Incidents & Postmortems (${incidents.length})`, icon: AlertTriangle },
              { id: 'data-rights', label: 'Data Rights Registry', icon: Database },
            ].map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as DevCommandTab)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all cursor-pointer ${
                    isActive
                      ? 'bg-[#1E40AF] text-white shadow-xs'
                      : 'bg-white text-[#475569] hover:bg-[#F1F5F9] border border-[#E2E8F0]'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>

          {killSwitchNotice && (
            <div className="p-4 mb-6 bg-blue-50 border border-blue-200 text-blue-900 rounded-xl text-sm font-bold">
              {killSwitchNotice}
            </div>
          )}

          {/* TAB 1: EXECUTIVE TELEMETRY */}
          {activeTab === 'overview' && (
            <div className="space-y-8">
              {/* Executive KPI Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-5">
                  <div className="text-xs font-bold text-[#64748B] uppercase tracking-wider mb-1">Active Dev Organizations</div>
                  <div className="text-3xl font-black text-[#0F172A]">{DEVELOPER_EXECUTIVE_METRICS.activeOrganizations}</div>
                  <div className="text-xs text-[#059669] font-bold mt-1">3,890 active projects</div>
                </div>

                <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-5">
                  <div className="text-xs font-bold text-[#64748B] uppercase tracking-wider mb-1">Monthly API Volume</div>
                  <div className="text-3xl font-black text-[#1E40AF]">{DEVELOPER_EXECUTIVE_METRICS.monthlyAPIRequests}</div>
                  <div className="text-xs text-[#059669] font-bold mt-1">99.98% success rate</div>
                </div>

                <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-5">
                  <div className="text-xs font-bold text-[#64748B] uppercase tracking-wider mb-1">Compute Units Metered</div>
                  <div className="text-3xl font-black text-[#0F172A]">{DEVELOPER_EXECUTIVE_METRICS.computeUnitsConsumedMonthly}</div>
                  <div className="text-xs text-[#64748B] font-bold mt-1">100% idempotent ledger</div>
                </div>

                <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-5">
                  <div className="text-xs font-bold text-[#64748B] uppercase tracking-wider mb-1">Active Partners</div>
                  <div className="text-3xl font-black text-[#0F172A]">{DEVELOPER_EXECUTIVE_METRICS.activePartners}</div>
                  <div className="text-xs text-[#059669] font-bold mt-1">100% security certified</div>
                </div>
              </div>

              {/* Traffic Distribution Table */}
              <div className="border border-[#E2E8F0] rounded-xl overflow-hidden bg-white">
                <div className="bg-[#F8FAFC] px-4 py-3 border-b border-[#E2E8F0] font-bold text-xs text-[#475569] uppercase tracking-wider">
                  Live API Product Performance Breakdown
                </div>
                <table className="w-full text-left text-sm">
                  <thead className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
                    <tr>
                      <th className="px-4 py-3 font-bold text-[#475569]">API Product</th>
                      <th className="px-4 py-3 font-bold text-[#475569]">Monthly Calls</th>
                      <th className="px-4 py-3 font-bold text-[#475569]">P95 Latency</th>
                      <th className="px-4 py-3 font-bold text-[#475569]">Error Rate</th>
                      <th className="px-4 py-3 font-bold text-[#475569]">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E2E8F0]">
                    {products.map((p) => (
                      <tr key={p.id} className="hover:bg-[#F8FAFC]">
                        <td className="px-4 py-3 font-bold text-[#0F172A]">{p.name}</td>
                        <td className="px-4 py-3 font-mono text-xs text-[#64748B]">{p.monthlyRequests.toLocaleString()}</td>
                        <td className="px-4 py-3 font-mono text-xs text-[#059669] font-bold">{p.p95LatencyMs} ms</td>
                        <td className="px-4 py-3 font-mono text-xs">{p.errorRatePct}%</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 text-xs font-bold rounded ${
                            p.status === 'ACTIVE' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'
                          }`}>
                            {p.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* TAB 2: PRODUCTS & TRAFFIC */}
          {activeTab === 'products-traffic' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-bold text-[#0F172A]">API Product Portfolio & Rate Limits</h3>
                <p className="text-sm text-[#64748B]">
                  Per-product compute weighting, active developer volume, and data governance classification.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {products.map((prod) => (
                  <div key={prod.id} className="border border-[#E2E8F0] rounded-2xl p-5 bg-white space-y-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-bold text-base text-[#0F172A]">{prod.name}</h4>
                        <div className="text-xs text-[#64748B] font-mono mt-0.5">Slug: {prod.slug}</div>
                      </div>
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs font-bold rounded">
                        {prod.stability}
                      </span>
                    </div>

                    <p className="text-xs text-[#64748B] leading-relaxed">{prod.description}</p>

                    <div className="grid grid-cols-2 gap-2 text-xs bg-[#F8FAFC] p-3 rounded-xl border border-[#E2E8F0]">
                      <div>
                        <span className="text-[#64748B]">Base Compute Weight:</span>
                        <strong className="block text-[#0F172A]">{prod.baseComputeUnits} Units</strong>
                      </div>
                      <div>
                        <span className="text-[#64748B]">Active Developers:</span>
                        <strong className="block text-[#0F172A]">{prod.activeDevelopers} Orgs</strong>
                      </div>
                    </div>

                    <div className="text-[11px] text-[#64748B] pt-2 border-t border-[#F1F5F9]">
                      Source Attribution: <strong>{prod.sourceAttribution}</strong>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* TAB 3: PARTNERS GOVERNANCE */}
          {activeTab === 'partners-governance' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-bold text-[#0F172A]">Partner Organization Governance & Quotas</h3>
                <p className="text-sm text-[#64748B]">
                  Enterprise contract references, security audits, and dedicated monthly compute allowances.
                </p>
              </div>

              <div className="border border-[#E2E8F0] rounded-xl overflow-hidden bg-white">
                <table className="w-full text-left text-sm">
                  <thead className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
                    <tr>
                      <th className="px-4 py-3 font-bold text-[#475569]">Partner Name</th>
                      <th className="px-4 py-3 font-bold text-[#475569]">Category</th>
                      <th className="px-4 py-3 font-bold text-[#475569]">Plan Tier</th>
                      <th className="px-4 py-3 font-bold text-[#475569]">Monthly Quota</th>
                      <th className="px-4 py-3 font-bold text-[#475569]">Security Review</th>
                      <th className="px-4 py-3 font-bold text-[#475569]">Contract Ref</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E2E8F0]">
                    {MOCK_PARTNERS.map((partner) => (
                      <tr key={partner.id}>
                        <td className="px-4 py-3 font-bold text-[#0F172A]">{partner.partnerName}</td>
                        <td className="px-4 py-3 text-xs text-[#64748B]">{partner.category}</td>
                        <td className="px-4 py-3 font-bold text-xs text-purple-700">{partner.planTier}</td>
                        <td className="px-4 py-3 font-mono text-xs">
                          {(partner.usedComputeUnits / 1000000).toFixed(1)}M / {(partner.monthlyQuotaComputeUnits / 1000000).toFixed(1)}M Units
                        </td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 text-xs font-bold rounded">
                            {partner.securityReviewStatus}
                          </span>
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-[#64748B]">{partner.contractReference}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* TAB 4: KILL SWITCHES & CIRCUIT BREAKERS */}
          {activeTab === 'kill-switches' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-bold text-[#0F172A]">Emergency API Kill Switches & Circuit Breakers</h3>
                <p className="text-sm text-[#64748B]">
                  Instantly disable specific API products or endpoints during upstream provider outages without taking down the platform.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {products.map((prod) => (
                  <div key={prod.id} className="border border-[#E2E8F0] rounded-2xl p-5 bg-white flex justify-between items-center">
                    <div>
                      <h4 className="font-bold text-sm text-[#0F172A]">{prod.name}</h4>
                      <p className="text-xs text-[#64748B] mt-0.5">{prod.endpointsCount} Endpoints — {prod.stability}</p>
                    </div>

                    <button
                      onClick={() => toggleProductStatus(prod.id)}
                      className={`px-4 py-2 rounded-xl text-xs font-bold flex items-center gap-2 transition-colors cursor-pointer ${
                        prod.status === 'ACTIVE'
                          ? 'bg-red-50 hover:bg-red-100 text-red-700 border border-red-200'
                          : 'bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200'
                      }`}
                    >
                      <Power className="w-3.5 h-3.5" />
                      <span>{prod.status === 'ACTIVE' ? 'Trip Kill Switch' : 'Restore Product'}</span>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* TAB 5: INCIDENTS */}
          {activeTab === 'incidents' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-bold text-[#0F172A]">Developer Incidents & Postmortems</h3>
                <p className="text-sm text-[#64748B]">
                  Transparent incident records with root-cause analysis and automated mitigation actions.
                </p>
              </div>

              <div className="space-y-4">
                {incidents.map((inc) => (
                  <div key={inc.id} className="border border-[#E2E8F0] rounded-2xl p-5 bg-white space-y-2">
                    <div className="flex justify-between items-start">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs font-bold text-[#1E40AF]">{inc.id}</span>
                        <h4 className="font-bold text-sm text-[#0F172A]">{inc.type}</h4>
                      </div>
                      <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 text-xs font-bold rounded">
                        {inc.status}
                      </span>
                    </div>
                    <p className="text-xs text-[#475569] leading-relaxed">{inc.description}</p>
                    <div className="p-3 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl text-xs text-[#059669] font-medium">
                      <strong>Mitigation:</strong> {inc.mitigationAction}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* TAB 6: DATA RIGHTS */}
          {activeTab === 'data-rights' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-bold text-[#0F172A]">Data Governance & Public Redistribution Rights</h3>
                <p className="text-sm text-[#64748B]">
                  Authoritative legal approvals ensuring zero unauthorized third-party commercial data reselling.
                </p>
              </div>

              <div className="border border-[#E2E8F0] rounded-xl overflow-hidden bg-white">
                <table className="w-full text-left text-sm">
                  <thead className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
                    <tr>
                      <th className="px-4 py-3 font-bold text-[#475569]">Dataset Name</th>
                      <th className="px-4 py-3 font-bold text-[#475569]">Primary Source</th>
                      <th className="px-4 py-3 font-bold text-[#475569]">Redistribution Class</th>
                      <th className="px-4 py-3 font-bold text-[#475569]">Legal Reviewer</th>
                      <th className="px-4 py-3 font-bold text-[#475569]">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E2E8F0]">
                    {MOCK_PUBLIC_DATA_RIGHTS.map((dr) => (
                      <tr key={dr.id}>
                        <td className="px-4 py-3 font-bold text-[#0F172A]">{dr.datasetName}</td>
                        <td className="px-4 py-3 text-xs text-[#64748B]">{dr.primarySource}</td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs font-bold rounded">
                            {dr.redistributionClass}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs text-[#64748B]">{dr.reviewer}</td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 text-xs font-bold rounded">
                            {dr.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
